import { ChangeDetectionStrategy, Component, Injector, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { AssistantService } from '@/cloud/services/assistant.service';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { AssistantSettings, UpdateAssistantSettings } from '@/cloud/models/assistant.models';

/**
 * Settings for the in-app assistant. With a `namespaceId` it manages that tenant's assistant (its own Anthropic
 * key, on/off and model); without one it manages the platform key used for service desk conversations.
 * The key is write-only: after saving, only its hint is ever shown.
 */
@Component({
    selector: 'app-assistant-settings-card',
    standalone: true,
    imports: [DatePipe, DecimalPipe, FormsModule, ButtonModule, InputTextModule, MessageModule, SelectModule, TagModule, ToggleSwitchModule, SectionCardComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: '../../pages/namespaces/namespace-detail/namespace-section.scss',
    template: `
        <app-section-card [title]="title()" [subtitle]="subtitle()" icon="pi pi-comments">
            @if (settings(); as s) {
                <p-tag badge [value]="s.enabled ? 'Enabled' : s.configured ? 'Off' : 'Not set up'" [severity]="s.enabled ? 'success' : 'secondary'" [rounded]="true" />
            }
            @if (settings(); as s) {
                <div class="ns-form">
                    @if (!s.encryptionAvailable) {
                        <p-message severity="warn">Secret encryption is not configured on this deployment, so a key can't be stored.</p-message>
                    }
                    @if (s.lastRejectedUtc) {
                        <div class="ns-callout" role="alert">
                            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                            <span>Anthropic rejected this key on {{ s.lastRejectedUtc | date: 'medium' }}. Replace it to bring the assistant back.</span>
                        </div>
                    }

                    <div class="ns-field">
                        <label for="assistant-key-{{ uid }}">Anthropic API key</label>
                        @if (s.configured && !replacing()) {
                            <div class="ns-inline">
                                <code class="ns-key">{{ s.keyHint }}</code>
                                @if (canManage()) {
                                    <p-button label="Replace" icon="pi pi-refresh" [text]="true" size="small" (onClick)="replacing.set(true)" />
                                    <p-button label="Remove" icon="pi pi-trash" severity="danger" [text]="true" size="small" [disabled]="saving()" (onClick)="remove()" />
                                }
                            </div>
                        } @else if (canManage()) {
                            <div class="ns-inline">
                                <input
                                    pInputText
                                    id="assistant-key-{{ uid }}"
                                    type="password"
                                    autocomplete="off"
                                    spellcheck="false"
                                    placeholder="sk-ant-…"
                                    [ngModel]="keyDraft()"
                                    (ngModelChange)="keyDraft.set($event)"
                                    (keydown.enter)="saveKey()"
                                    [disabled]="saving() || !s.encryptionAvailable"
                                />
                                <p-button label="Save key" icon="pi pi-check" [loading]="saving()" [disabled]="!keyDraft().trim() || !s.encryptionAvailable" (onClick)="saveKey()" />
                                @if (replacing()) {
                                    <p-button label="Cancel" [text]="true" (onClick)="replacing.set(false); keyDraft.set('')" />
                                }
                            </div>
                            <small>{{ keyHelp() }} The key is checked with Anthropic, then stored encrypted. Nobody can read it back.</small>
                        } @else {
                            <p class="ns-meta">No key has been added.</p>
                        }
                    </div>

                    <div class="ns-field">
                        <label for="assistant-model-{{ uid }}">Model</label>
                        <p-select
                            inputId="assistant-model-{{ uid }}"
                            [options]="s.models"
                            optionLabel="label"
                            optionValue="id"
                            [ngModel]="s.model"
                            (ngModelChange)="save({ model: $event }, 'Model updated')"
                            [disabled]="saving() || !canManage()"
                            class="w-full md:w-96"
                        />
                    </div>

                    <div class="ns-switch">
                        <p-toggleswitch [ngModel]="s.enabled" (ngModelChange)="setEnabled($event)" [disabled]="saving() || !canManage() || !s.configured" inputId="assistant-enabled-{{ uid }}" />
                        <label for="assistant-enabled-{{ uid }}">{{ toggleLabel() }}</label>
                    </div>
                    @if (!s.configured) {
                        <p class="ns-meta">Add a key to enable it.</p>
                    }

                    @if (s.usage; as u) {
                        <div class="usage">
                            <span
                                ><strong>{{ u.conversations | number }}</strong> conversations</span
                            >
                            <span
                                ><strong>{{ u.inputTokens | number }}</strong> input tokens</span
                            >
                            <span
                                ><strong>{{ u.outputTokens | number }}</strong> output tokens</span
                            >
                            <span class="ns-meta">last 30 days</span>
                        </div>
                    }
                    @if (s.updatedUtc) {
                        <p class="ns-meta">Last changed {{ s.updatedUtc | date: 'medium' }}</p>
                    }
                    @if (!canManage()) {
                        <p class="ns-meta">Only tenant admins can change this.</p>
                    }
                </div>
            } @else if (error()) {
                <p-message severity="error">Could not load the assistant settings.</p-message>
            }
        </app-section-card>
    `,
    styles: [
        `
            .usage {
                display: flex;
                flex-wrap: wrap;
                align-items: baseline;
                gap: 0.5rem 1.25rem;
                padding: 0.75rem 1rem;
                border-radius: var(--app-card-radius, 0.75rem);
                background: var(--surface-ground);
                font-size: 0.9rem;
            }
        `
    ]
})
export class AssistantSettingsCardComponent {
    /** Which key this card manages: a tenant's (with <code>namespaceId</code>) or the platform's service desk key. */
    readonly scope = input<'tenant' | 'platform'>('tenant');
    /** Tenant to manage; required for the tenant scope. Until it is known the card waits rather than guessing. */
    readonly namespaceId = input<string | null>(null);
    /** Whether the current user may change it (tenant: namespace.manage; platform: superadmin). */
    readonly canManage = input(false);

    private api = inject(CloudApiService);
    private injector = inject(Injector);
    private messages = inject(MessageService);

    readonly uid = Math.random().toString(36).slice(2, 8);
    readonly settings = signal<AssistantSettings | null>(null);
    readonly error = signal(false);
    readonly saving = signal(false);
    readonly replacing = signal(false);
    readonly keyDraft = signal('');

    readonly platform = computed(() => this.scope() === 'platform');
    /** Responses from a superseded load (e.g. before the tenant id arrived) must not overwrite a newer one. */
    private loadRequest = 0;
    readonly title = computed(() => (this.platform() ? 'Service desk assistant' : 'Assistant'));
    readonly subtitle = computed(() =>
        this.platform()
            ? 'The Anthropic key used when people with service desk access ask the assistant about every tenant at once. Tenant conversations use each tenant’s own key.'
            : 'A chat assistant in the dashboard that answers questions about this tenant’s devices, rooms and problems, and can propose actions such as a reboot for someone to confirm. It uses this tenant’s own Anthropic API key.'
    );
    readonly toggleLabel = computed(() => (this.platform() ? 'Allow service desk conversations' : 'Enable the assistant for this tenant'));
    readonly keyHelp = computed(() => (this.platform() ? 'Create a key in the Anthropic Console for the platform.' : 'Create a key in the Anthropic Console for this tenant’s organisation.'));

    constructor() {
        effect(() => {
            const ns = this.namespaceId();
            untracked(() => this.load(ns));
        });
    }

    async load(ns = this.namespaceId()): Promise<void> {
        const platform = this.platform();
        if (!platform && !ns) return;
        const request = ++this.loadRequest;
        this.error.set(false);
        try {
            const settings = platform ? await this.api.getPlatformAssistantSettings() : await this.api.getNamespaceAssistantSettings(ns!);
            if (request === this.loadRequest) this.settings.set(settings);
        } catch {
            if (request === this.loadRequest) this.error.set(true);
        }
    }

    saveKey(): void {
        const key = this.keyDraft().trim();
        if (!key) return;
        this.save({ apiKey: key }, 'API key saved', () => {
            this.keyDraft.set('');
            this.replacing.set(false);
        });
    }

    setEnabled(enabled: boolean): void {
        this.save({ enabled }, enabled ? 'Assistant enabled' : 'Assistant disabled');
    }

    async save(update: UpdateAssistantSettings, done: string, after?: () => void): Promise<void> {
        const ns = this.namespaceId();
        this.saving.set(true);
        try {
            this.settings.set(this.platform() ? await this.api.updatePlatformAssistantSettings(update) : await this.api.updateNamespaceAssistantSettings(ns!, update));
            after?.();
            this.messages.add({ severity: 'success', summary: done });
            // Refresh the top-bar button for this tenant. Resolved lazily: the settings pages don't otherwise need it.
            if (!this.platform() && ns)
                this.injector
                    .get(AssistantService)
                    .loadStatus(ns, true)
                    .catch(() => undefined);
        } catch (err) {
            this.messages.add({ severity: 'error', summary: 'Could not save', detail: AssistantSettingsCardComponent.errorText(err) });
            // Re-render controls at their stored values.
            this.settings.update((s) => (s ? { ...s } : s));
        } finally {
            this.saving.set(false);
        }
    }

    async remove(): Promise<void> {
        const ns = this.namespaceId();
        this.saving.set(true);
        try {
            if (this.platform()) await this.api.removePlatformAssistantKey();
            else await this.api.removeNamespaceAssistantKey(ns!);
            this.messages.add({ severity: 'success', summary: 'Key removed and assistant turned off' });
            await this.load(ns);
        } catch (err) {
            this.messages.add({ severity: 'error', summary: 'Could not remove the key', detail: AssistantSettingsCardComponent.errorText(err) });
        } finally {
            this.saving.set(false);
        }
    }

    static errorText(err: unknown): string {
        const e = err as { error?: { error?: string; message?: string } | string; message?: string } | null;
        if (typeof e?.error === 'string') return e.error;
        return e?.error?.error ?? e?.error?.message ?? 'Try again.';
    }
}
